"use client";

/**
 * Bloc « Crédibilité / À propos » localisé (FR / EN / AR).
 *
 * Tout le texte vient de `ABOUT_UI[locale]` via `tr()` (repli FR). Les images,
 * icônes et la mise en page restent identiques quelle que soit la locale ;
 * seules les chaînes changent. Défaut `locale = "fr"` → sortie FR inchangée.
 * Les images sont appariées positionnellement aux entrées texte du dico.
 */
import Image from "next/image";
import { motion } from "framer-motion";
import BrandSignature3D from "@/components/branding/BrandSignature3D";
import landingHeroBrandFilm from "@/assets/images/landing/Hero-brand-film.png";
import landingSocialProof from "@/assets/images/landing/Preuve-sociale.png";
import landingFleetUniverse from "@/assets/images/landing/Univers-flotte.png";
import ambianceInterieurPremium from "@/assets/images/chauffeur/Ambiance-interieur-premium.png";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { ABOUT_UI } from "./aboutDict";

// Images des « preuves terrain », appariées par position aux entrées
// `proof.moments` du dico (ordre stable).
const PROOF_IMAGES = [
  landingHeroBrandFilm,
  ambianceInterieurPremium,
  landingFleetUniverse,
];

export default function CredibilityExperience({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const t = tr(ABOUT_UI, locale);

  return (
    <>
      <section className="relative overflow-hidden border-b border-black/10 bg-white py-16 md:py-20">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_20%,rgba(191,161,104,0.12),transparent_42%),radial-gradient(circle_at_85%_15%,rgba(0,0,0,0.08),transparent_35%)]" />
        <div className="mx-auto grid max-w-[90rem] gap-8 px-6 md:px-8 lg:grid-cols-[1.08fr_0.92fr]">
          <div className="flex flex-col justify-center">
            <motion.p
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.45 }}
              className="mb-3 text-xs uppercase tracking-[0.28em] text-black/55"
            >
              {t.hero.eyebrow}
            </motion.p>
            <motion.h1
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="max-w-4xl font-space-age text-3xl tracking-[0.14em] text-black md:text-5xl"
            >
              {t.hero.title}
            </motion.h1>
            <motion.p
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="mt-5 max-w-2xl text-sm text-black/75 md:text-base"
            >
              {t.hero.intro}
            </motion.p>
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="mt-7 grid gap-2 sm:grid-cols-2"
            >
              {t.hero.trustSignals.map((signal) => (
                <div
                  key={signal}
                  className="rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-black/80 shadow-[0_1px_0_rgba(0,0,0,0.05)]"
                >
                  {signal}
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.55 }}
            className="relative h-full min-h-[340px] overflow-hidden rounded-[2rem] border border-black/10 bg-black/5"
          >
            <Image
              src={landingSocialProof}
              alt={t.hero.imageAlt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 45vw"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white">
              <p className="text-xs uppercase tracking-[0.22em] text-white/70">
                {t.hero.reputationEyebrow}
              </p>
              <p className="mt-2 max-w-md text-sm text-white/90">
                {t.hero.reputationText}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="preuves" className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <div className="mb-6">
            <p className="mb-2 text-xs uppercase tracking-[0.2em] text-black/55">
              {t.proof.eyebrow}
            </p>
            <h2 className="font-space-age text-xl tracking-[0.18em] text-black md:text-2xl">
              {t.proof.heading}
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {t.proof.moments.map((item, index) => (
              <motion.article
                key={item.title}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-12%" }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                whileHover={{
                  y: -6,
                  rotateX: 2,
                  rotateY: index === 1 ? -2 : 2,
                }}
                style={{ transformStyle: "preserve-3d" }}
                className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_14px_40px_rgba(0,0,0,0.08)]"
              >
                <div className="relative aspect-[16/10]">
                  <Image
                    src={PROOF_IMAGES[index]}
                    alt={item.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-black">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-black/70">{item.text}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto grid max-w-[90rem] gap-6 px-6 md:px-8 lg:grid-cols-[1fr_0.92fr]">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.2em] text-black/55">
              {t.process.eyebrow}
            </p>
            <h2 className="mb-5 font-space-age text-xl tracking-[0.18em] text-black md:text-2xl">
              {t.process.heading}
            </h2>
            <div className="grid gap-3">
              {t.process.steps.map((item, index) => (
                <motion.article
                  key={item.step}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-12%" }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="rounded-2xl border border-black/10 bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.05)]"
                >
                  <p className="text-xs uppercase tracking-[0.18em] text-black/50">
                    {t.process.stepLabel} {item.step}
                  </p>
                  <h3 className="mt-2 text-base font-semibold text-black">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-black/70">{item.text}</p>
                </motion.article>
              ))}
            </div>
          </div>

          <motion.aside
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-[2rem] border border-black/10 bg-black p-6 text-white shadow-[0_24px_70px_rgba(0,0,0,0.35)]"
          >
            <div className="absolute inset-0">
              <BrandSignature3D accentColor="#f3cf86" />
            </div>
            <div className="pointer-events-none absolute inset-0 bg-black/55" />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[0.22em] text-gold-200/85">
                {t.process.aside.eyebrow}
              </p>
              <h3 className="mt-2 font-space-age text-lg tracking-[0.18em]">
                {t.process.aside.title}
              </h3>
              <p className="mt-3 max-w-sm text-sm text-white/80">
                {t.process.aside.text}
              </p>
            </div>
          </motion.aside>
        </div>
      </section>

      <section className="border-y border-black/10 bg-secondary/30 py-14 text-black md:py-16">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <p className="mb-2 text-xs uppercase tracking-[0.2em] text-black/55">
            {t.quality.eyebrow}
          </p>
          <h2 className="mb-5 font-space-age text-xl tracking-[0.18em] md:text-2xl">
            {t.quality.heading}
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {t.quality.commitments.map((item, index) => (
              <motion.article
                key={item.title}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                whileHover={{ scale: 1.01 }}
                className="rounded-2xl border border-black/10 bg-white p-5"
              >
                <h3 className="text-base font-semibold text-black">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-black/70">{item.text}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
